import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import qcService, {
  QaQcDashboardSummary,
  QaQcDashboardFilters,
  QaQcCrmChart,
  QcChartPoint,
  QcChartSummary,
} from '../../services/qcService';
import clientsService from '../../services/clientsService';
import LeveyJenningsChart from './LeveyJenningsChart';

const LABS = [
  { value: '', label: 'Tous laboratoires' },
  { value: 'LABORATOIRE_ICP', label: 'ICP' },
  { value: 'LABORATOIRE_SAA', label: 'SAA' },
  { value: 'LABORATOIRE_AU', label: 'Au' },
  { value: 'LABORATOIRE_PHYSICO_CHIMIQUE', label: 'Physico-chimique' },
  { value: 'PREP_MECANIQUE', label: 'Préparation mécanique' },
  { value: 'INSTRUMENT', label: 'Instrument (QC interne)' },
];

const SOURCES = [
  { value: 'both', label: 'Tout (réception + instrument)' },
  { value: 'reception', label: 'Réception (BIS / Blanc / MRC)' },
  { value: 'instrument', label: 'Instrument / CSV (OREAS, blancs)' },
];

interface ClientOption {
  id: string;
  company_name: string;
}

/**
 * Adapt a unified CRM chart into the shape `LeveyJenningsChart` expects so we
 * reuse the exact same rendering used in the per-chart admin view.
 */
function toLjProps(c: QaQcCrmChart): {
  summary: QcChartSummary;
  points: QcChartPoint[];
} {
  const summary: QcChartSummary = {
    chart_id: `${c.source}:${c.laboratory}:${c.ref_label}:${c.element}`,
    laboratory: c.laboratory,
    element: c.element,
    control_type: 'CRM_RECOVERY',
    reference_material: c.ref_label,
    total_points: c.n,
    in_control_percentage:
      c.n > 0
        ? (c.points.filter((p) => Math.abs(p.z) < 2).length / c.n) * 100
        : 100,
    current_mean: c.mean,
    current_std_dev: c.sd,
    baseline_mean: c.mean,
    baseline_std_dev: c.sd,
    ucl: c.ucl,
    lcl: c.lcl,
    uwl: c.uwl,
    lwl: c.lwl,
  };
  const points: QcChartPoint[] = c.points.map((p, i) => ({
    id: `${i}`,
    chart_id: summary.chart_id,
    laboratory: c.laboratory,
    element: c.element,
    control_type: 'CRM_RECOVERY',
    reference_material: c.ref_label,
    value: p.value,
    date: p.date || new Date().toISOString(),
    batch_id: p.batch_number || '',
    mean: c.mean,
    standard_deviation: c.sd,
    ucl: c.ucl,
    lcl: c.lcl,
    uwl: c.uwl,
    lwl: c.lwl,
    out_of_control: Math.abs(p.z) >= 3,
    warning_level: Math.abs(p.z) >= 2 && Math.abs(p.z) < 3,
    rule_violations: Math.abs(p.z) >= 3 ? { '1_3s': true } : {},
  }));
  return { summary, points };
}

const SourceChip: React.FC<{ source: string }> = ({ source }) => (
  <Chip
    size="small"
    label={source === 'instrument' ? 'Instrument' : 'Réception'}
    color={source === 'instrument' ? 'secondary' : 'primary'}
    variant="outlined"
    sx={{ height: 20, fontSize: 10 }}
  />
);

const fmt = (v: number | null | undefined, d = 2) =>
  v === null || v === undefined || !Number.isFinite(Number(v))
    ? '—'
    : Number(v).toFixed(d);

const UnifiedQaQcTab: React.FC = () => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [filters, setFilters] = useState<QaQcDashboardFilters>({ source: 'both' });
  const [data, setData] = useState<QaQcDashboardSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    clientsService
      .getActiveClients()
      .then((list) =>
        setClients(list.map((c) => ({ id: c.id, company_name: c.company_name }))),
      )
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await qcService.getDashboardSummary(filters);
      setData(res);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement de la vue QA/QC');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = data?.stats;

  const exportCsv = () => {
    if (!data) return;
    const rows: string[][] = [
      ['section', 'source', 'ref', 'element', 'n', 'moyenne', 'sd', 'cv%', 'violations'],
      ...data.recovery.map((r) => [
        'recovery',
        r.source,
        r.ref_label,
        r.element,
        String(r.n),
        fmt(r.observed_mean),
        fmt(r.observed_sd),
        fmt(r.cv_percent),
        String(r.violations_count),
      ]),
      ['', '', '', '', '', '', '', '', ''],
      ['violation', 'source', 'ref', 'element', 'regle', 'date', 'lot', 'valeur', ''],
      ...data.violations.map((v) => [
        'violation',
        v.source,
        v.ref_label,
        v.element,
        v.rule,
        v.date ? new Date(v.date).toLocaleString('fr-FR') : '',
        v.batch_number || '',
        fmt(v.value, 3),
        '',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qaqc-unifie-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const crmCharts = data?.crm_charts || [];
  const blankCharts = data?.blank_charts || [];
  const duplicates = data?.duplicates || [];
  const recovery = data?.recovery || [];
  const violations = data?.violations || [];

  const adapted = useMemo(() => crmCharts.map(toLjProps), [crmCharts]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Vue agrégée <strong>auto-alimentée</strong> : QC de réception (BIS, Blanc,
        MRC — valeurs issues de l'import CSV) et QC interne de l'instrument (OREAS,
        blancs du CSV). Aucune saisie manuelle. Lecture seule.
      </Alert>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }} className="no-print">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={clients}
              getOptionLabel={(o) => o.company_name}
              value={clients.find((c) => c.id === filters.client_id) || null}
              onChange={(_, v) =>
                setFilters((f) => ({ ...f, client_id: v?.id }))
              }
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Laboratoire</InputLabel>
              <Select
                label="Laboratoire"
                value={filters.laboratory || ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    laboratory: (e.target.value as string) || undefined,
                  }))
                }
              >
                {LABS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                label="Source"
                value={filters.source || 'both'}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, source: e.target.value as any }))
                }
              >
                {SOURCES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Élément"
              value={filters.element || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, element: e.target.value || undefined }))
              }
              placeholder="Cu, Pb, ..."
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.from || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, from: e.target.value || undefined }))
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.to || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, to: e.target.value || undefined }))
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={load}
                disabled={loading}
              >
                Charger
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportCsv}
                disabled={!data}
              >
                Exporter CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
              >
                Imprimer
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Cartes MRC/CRM', value: stats?.crm_series ?? 0 },
          { label: 'Séries blancs', value: stats?.blank_series ?? 0 },
          { label: 'Duplicatas', value: stats?.duplicate_points ?? 0 },
          { label: 'Lignes récap.', value: stats?.recovery_rows ?? 0 },
          { label: 'Violations', value: stats?.violations ?? 0, warn: true },
        ].map((s) => (
          <Grid item xs={6} md key={s.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
                <Typography
                  variant="h5"
                  color={s.warn && s.value > 0 ? 'error' : 'text.primary'}
                >
                  {s.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" className="no-print">
          <Tab label={`Cartes CRM (${crmCharts.length})`} />
          <Tab label={`Violations (${violations.length})`} />
          <Tab label={`Blancs (${blankCharts.length})`} />
          <Tab label={`Duplicatas (${duplicates.length})`} />
          <Tab label={`Récapitulatif (${recovery.length})`} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* CRM charts */}
              {tab === 0 &&
                (adapted.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Grid container spacing={2}>
                    {adapted.map((a, i) => (
                      <Grid item xs={12} md={6} key={a.summary.chart_id + i}>
                        <Card variant="outlined">
                          <CardContent>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle2">
                                {crmCharts[i].ref_label} — {crmCharts[i].element}
                              </Typography>
                              <SourceChip source={crmCharts[i].source} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              n={crmCharts[i].n} · moy {fmt(crmCharts[i].mean)} ·
                              σ {fmt(crmCharts[i].sd)}
                            </Typography>
                            <LeveyJenningsChart
                              summary={a.summary}
                              points={a.points}
                              height={240}
                              yLabel="Récupération (%)"
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ))}

              {/* Violations */}
              {tab === 1 &&
                (violations.length === 0 ? (
                  <EmptyState label="Aucune violation Westgard." />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Lot</TableCell>
                          <TableCell>Référence</TableCell>
                          <TableCell>Élément</TableCell>
                          <TableCell>Règle</TableCell>
                          <TableCell align="right">Valeur</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {violations.map((v, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <SourceChip source={v.source} />
                            </TableCell>
                            <TableCell>
                              {v.date
                                ? new Date(v.date).toLocaleDateString('fr-FR')
                                : '—'}
                            </TableCell>
                            <TableCell>{v.batch_number || '—'}</TableCell>
                            <TableCell>{v.ref_label}</TableCell>
                            <TableCell>{v.element}</TableCell>
                            <TableCell>
                              <Chip size="small" color="error" label={v.rule} />
                            </TableCell>
                            <TableCell align="right">{fmt(v.value, 3)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))}

              {/* Blanks */}
              {tab === 2 &&
                (blankCharts.length === 0 ? (
                  <EmptyState label="Aucune série de blancs." />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Élément</TableCell>
                          <TableCell>Laboratoire</TableCell>
                          <TableCell align="right">n</TableCell>
                          <TableCell align="right">Max</TableCell>
                          <TableCell align="right">UCL</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blankCharts.map((b, i) => {
                          const max = b.points.reduce(
                            (m, p) => Math.max(m, p.value),
                            0,
                          );
                          return (
                            <TableRow key={i}>
                              <TableCell>
                                <SourceChip source={b.source} />
                              </TableCell>
                              <TableCell>{
                                b.blank_type === 'PREP_CONCASSAGE_BLANK' ? 'Blanc Concassage'
                                : b.blank_type === 'PREP_BROYAGE_BLANK' ? 'Blanc Broyage'
                                : b.blank_type === 'PREP_CONCASSAGE_QUALITY' ? 'Qualité de concassage'
                                : b.blank_type === 'PREP_BROYAGE_QUALITY' ? 'Qualité de broyage'
                                : b.blank_type === 'BLANK' ? 'Blanc analytique'
                                : b.blank_type
                              }</TableCell>
                              <TableCell>{b.element}</TableCell>
                              <TableCell>{b.laboratory}</TableCell>
                              <TableCell align="right">{b.n}</TableCell>
                              <TableCell align="right">{fmt(max, 4)}</TableCell>
                              <TableCell align="right">
                                {b.ucl === null ? '—' : fmt(b.ucl, 4)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))}

              {/* Duplicates */}
              {tab === 3 &&
                (duplicates.length === 0 ? (
                  <EmptyState label="Aucun duplicata (RPD)." />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Lot</TableCell>
                          <TableCell>Élément</TableCell>
                          <TableCell align="right">RPD %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {duplicates.map((d, i) => (
                          <TableRow
                            key={i}
                            sx={d.rpd > 20 ? { bgcolor: 'error.50' } : undefined}
                          >
                            <TableCell>
                              <SourceChip source={d.source} />
                            </TableCell>
                            <TableCell>
                              {d.date
                                ? new Date(d.date).toLocaleDateString('fr-FR')
                                : '—'}
                            </TableCell>
                            <TableCell>{d.batch_number || '—'}</TableCell>
                            <TableCell>{d.element}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                color={d.rpd > 20 ? 'error' : 'text.primary'}
                              >
                                {fmt(d.rpd, 1)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))}

              {/* Recovery summary */}
              {tab === 4 &&
                (recovery.length === 0 ? (
                  <EmptyState label="Aucune donnée de récupération." />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Référence</TableCell>
                          <TableCell>Élément</TableCell>
                          <TableCell align="right">n</TableCell>
                          <TableCell align="right">Moyenne</TableCell>
                          <TableCell align="right">σ</TableCell>
                          <TableCell align="right">CV %</TableCell>
                          <TableCell align="right">Violations</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recovery.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <SourceChip source={r.source} />
                            </TableCell>
                            <TableCell>{r.ref_label}</TableCell>
                            <TableCell>{r.element}</TableCell>
                            <TableCell align="right">{r.n}</TableCell>
                            <TableCell align="right">
                              {fmt(r.observed_mean)}
                            </TableCell>
                            <TableCell align="right">
                              {fmt(r.observed_sd)}
                            </TableCell>
                            <TableCell align="right">
                              {fmt(r.cv_percent)}
                            </TableCell>
                            <TableCell align="right">
                              {r.violations_count > 0 ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={r.violations_count}
                                />
                              ) : (
                                0
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

const EmptyState: React.FC<{ label?: string }> = ({ label }) => (
  <Box sx={{ p: 5, textAlign: 'center' }}>
    <Typography color="text.secondary">
      {label || 'Aucune donnée pour ces filtres. Cliquez sur « Charger ».'}
    </Typography>
  </Box>
);

export default UnifiedQaQcTab;
